import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api, { evidencePackSharingApi, type EvidencePackShare, type ShareFormat } from '../../lib/api';
import { appLinks } from '../../lib/crossSiteLinks';
import { useAuthStore } from '../../stores/authStore';

type ShareWatermarkMode = 'Off' | 'Trial';

interface BillingStatusShareEntitlements {
  planCode?: string | null;
  canShareEvidencePacks: boolean;
  maxShareExpiryDays: number | null;
  shareAllowedFormats: string | null;
  shareWatermarkMode: ShareWatermarkMode | null;
  maxActiveShareLinks: number | null;
  maxShareDownloadsPerLink: number | null;
}

const FORMAT_LABELS: Record<ShareFormat, string> = {
  pdf: 'PDF',
  csv: 'CSV',
  json: 'JSON',
  zip: 'ZIP',
};

const FORMAT_HELP: Record<ShareFormat, string> = {
  pdf: 'Formatted report',
  csv: 'Spreadsheet export',
  json: 'Machine-readable',
  zip: 'All files bundled',
};

const SORTED_FORMATS: ShareFormat[] = ['pdf', 'csv', 'json', 'zip'];

interface ShareEvidencePackModalProps {
  packId: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function ShareEvidencePackModal({
  packId,
  isOpen,
  onClose,
}: ShareEvidencePackModalProps) {
  const tenantContext = useAuthStore((state) => state.tenantContext);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [expiryDays, setExpiryDays] = useState<number>(14);
  const [selectedFormat, setSelectedFormat] = useState<ShareFormat>('pdf');
  const [shareData, setShareData] = useState<EvidencePackShare | null>(null);

  const { data: shareEntitlements } = useQuery<BillingStatusShareEntitlements>({
    queryKey: ['billing', 'status', 'share', tenantContext?.tenantId],
    queryFn: async () => {
      const response = await api.get('/v1/billing/status');
      return response.data as BillingStatusShareEntitlements;
    },
    enabled: isAuthenticated && !!tenantContext?.tenantId,
    staleTime: 60_000,
  });

  const maxExpiryDays = shareEntitlements?.maxShareExpiryDays ?? null;
  const canShare = shareEntitlements?.canShareEvidencePacks ?? true;
  const isEarlyAccess = shareEntitlements?.planCode === 'EARLY_ACCESS';

  const allowedFormats = useMemo(() => {
    const raw = shareEntitlements?.shareAllowedFormats ?? '';
    if (!raw) {
      return SORTED_FORMATS;
    }
    const normalized = raw
      .split(',')
      .map((entry) => entry.trim().toLowerCase())
      .filter((entry) => entry.length > 0);
    if (normalized.includes('all')) {
      return SORTED_FORMATS;
    }
    const allowed = SORTED_FORMATS.filter((format) => normalized.includes(format));
    return allowed.length > 0 ? allowed : SORTED_FORMATS;
  }, [shareEntitlements?.shareAllowedFormats]);

  useEffect(() => {
    if (!allowedFormats.includes(selectedFormat)) {
      setSelectedFormat(allowedFormats[0] ?? 'pdf');
    }
  }, [allowedFormats, selectedFormat]);

  const expiryOptions = useMemo(() => {
    const options = [7, 14, 30, 60, 90].filter((days) => !maxExpiryDays || days <= maxExpiryDays);
    if (!options.includes(expiryDays)) {
      options.push(expiryDays);
    }
    return options.sort((a, b) => a - b);
  }, [expiryDays, maxExpiryDays]);

  const isExpiryInvalid = maxExpiryDays !== null && expiryDays > maxExpiryDays;

  const watermarkMode: ShareWatermarkMode =
    (shareData?.watermarkMode as ShareWatermarkMode | undefined) ??
    shareEntitlements?.shareWatermarkMode ??
    'Off';

  const createShareMutation = useMutation({
    mutationFn: (payload: { days: number; format: ShareFormat }) =>
      evidencePackSharingApi.createShare(packId, payload.days, payload.format),
    onSuccess: (response) => {
      setShareData(response.data);
      toast.success('Share link created');
    },
    onError: () => {
      toast.error('Failed to create share link');
    },
  });

  const revokeShareMutation = useMutation({
    mutationFn: (shareId: string) => evidencePackSharingApi.revokeShare(shareId),
    onSuccess: () => {
      toast.success('Share link revoked');
      setShareData(null);
      onClose();
    },
    onError: () => {
      toast.error('Failed to revoke share link');
    },
  });

  const handleCreateLink = () => {
    createShareMutation.mutate({ days: expiryDays, format: selectedFormat });
  };

  const handleCopyLink = async () => {
    if (!shareData?.shareUrl) return;
    
    try {
      await navigator.clipboard.writeText(shareData.shareUrl);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleRevoke = () => {
    if (!shareData?.shareId) return;
    
    if (window.confirm('Are you sure you want to revoke this share link? It will no longer be accessible.')) {
      revokeShareMutation.mutate(shareData.shareId);
    }
  };

  const handleClose = () => {
    setShareData(null);
    setExpiryDays(14);
    setSelectedFormat('pdf');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div 
        className="bg-slate-800 rounded-lg p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-white mb-4">Share Evidence Pack</h2>
        
        {!shareData ? (
          <>
            <p className="text-slate-400 text-sm mb-4">
              Create a time-limited public link to share this evidence pack. Anyone with the link can view and download it.
            </p>

            {!canShare && (
              <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-900/20 px-4 py-3">
                <p className="text-amber-200 text-sm font-medium">
                  {isEarlyAccess ? 'Sharing is limited during Early Access.' : 'Sharing is available on paid plans.'}
                </p>
                <p className="text-amber-200/80 text-xs mt-1">
                  {isEarlyAccess ? (
                    <>
                      Tell us what sharing workflow you need in
                      {' '}
                      <a href={`${appLinks.settings}#feedback`} className="underline hover:text-amber-100">Settings → Feedback</a>.
                    </>
                  ) : (
                    'Upgrade to create evidence pack share links.'
                  )}
                </p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm text-slate-400 mb-2">
                Link expires after
              </label>
              <div className="grid grid-cols-3 gap-2">
                {expiryOptions.map((option) => (
                  <button
                    key={option}
                    onClick={() => setExpiryDays(option)}
                    className={`px-4 py-2 rounded text-sm font-medium transition ${
                      expiryDays === option
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {option} days
                  </button>
                ))}
              </div>
              {maxExpiryDays !== null && (
                <p className="mt-2 text-xs text-slate-400">Max {maxExpiryDays} days on your plan.</p>
              )}
              {isExpiryInvalid && (
                <p className="mt-2 text-xs text-red-300">Selected expiry exceeds your plan limit.</p>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm text-slate-400 mb-2">Download format</label>
              <div className="grid grid-cols-2 gap-2">
                {allowedFormats.map((format) => (
                  <button
                    key={format}
                    onClick={() => setSelectedFormat(format)}
                    className={`px-3 py-2 rounded text-sm font-medium transition border ${
                      selectedFormat === format
                        ? 'bg-blue-600 text-white border-blue-500'
                        : 'bg-slate-700 text-slate-300 border-slate-600 hover:bg-slate-600'
                    }`}
                  >
                    {FORMAT_LABELS[format]}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-400">
                {FORMAT_HELP[selectedFormat]}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-slate-700 text-slate-300 px-4 py-2 rounded font-medium hover:bg-slate-600 transition"
                disabled={createShareMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateLink}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-blue-700 transition disabled:opacity-50"
                disabled={createShareMutation.isPending || !canShare || isExpiryInvalid}
              >
                {createShareMutation.isPending ? 'Creating...' : 'Create Link'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-slate-400 text-sm mb-4">
              Your share link is ready. Anyone with this link can access the evidence pack until it expires.
            </p>

            <div className="mb-4 p-3 bg-slate-700 rounded border border-slate-600">
              <p className="text-xs text-slate-400 mb-1">Share URL</p>
              <p className="font-mono text-sm break-all text-white mb-2">{shareData.shareUrl}</p>
              <button
                onClick={handleCopyLink}
                className="w-full bg-blue-600 text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-blue-700 transition"
              >
                Copy Link
              </button>
            </div>

            <div className="mb-6 p-3 bg-slate-700 rounded border border-slate-600">
              <p className="text-xs text-slate-400 mb-1">Expires</p>
              <p className="text-sm text-white">
                {new Date(shareData.expiresAt).toLocaleString()}
              </p>
            </div>

            {watermarkMode === 'Trial' && (
              <div className="mb-6 rounded border border-amber-500/40 bg-amber-900/20 px-4 py-3">
                <p className="text-amber-200 text-sm font-medium">Trial watermark applied</p>
                <p className="text-amber-200/80 text-xs mt-1">
                  Shared downloads include a trial watermark on this plan.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 bg-slate-700 text-slate-300 px-4 py-2 rounded font-medium hover:bg-slate-600 transition"
                disabled={revokeShareMutation.isPending}
              >
                Done
              </button>
              <button
                type="button"
                onClick={handleRevoke}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded font-medium hover:bg-red-700 transition disabled:opacity-50"
                disabled={revokeShareMutation.isPending}
              >
                {revokeShareMutation.isPending ? 'Revoking...' : 'Revoke Link'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
